// client/src/coms/settings/sections/AudioDeviceSettings.js
// Personal Settings → Audio Devices: microphone and output choice (this browser), permissions and a level test.
import React, { useState } from 'react';
import { Row, Section } from '../../personalisation/settingsControls';
import { canSetSinkId } from '../useDeviceSettings';
import useMicMeter from '../useMicMeter';

export function DeviceSelect({ label, value, options, onChange, disabled, emptyText, testId, defaultLabel = 'System default' }) {
  const known = !value || options.some((o) => o.id === value);
  return (
    <select className="vx-select vx-device-select" aria-label={label} value={value || ''} disabled={disabled} onChange={(e) => onChange(e.target.value)} data-testid={testId}>
      {defaultLabel ? <option value="">{defaultLabel}</option> : null}
      {!known ? <option value={value}>Saved device (not connected)</option> : null}
      {options.map((o) => (
        <option key={o.id} value={o.id}>
          {o.label}
        </option>
      ))}
      {!options.length && emptyText ? (
        <option value="__none" disabled>
          {emptyText}
        </option>
      ) : null}
    </select>
  );
}

const pctOfDb = (db) => Math.max(0, Math.min(100, ((Math.max(-80, Math.min(-10, db)) + 80) / 70) * 100));

export function LevelMeter({ db, gateDb, label = 'Microphone level' }) {
  const over = db >= gateDb;
  return (
    <div className="vx-meter" role="meter" aria-label={label} aria-valuemin={-80} aria-valuemax={-10} aria-valuenow={Math.round(Math.max(-80, Math.min(-10, db)))} aria-valuetext={`${Math.round(db)} dB${over ? ', above the sensitivity line' : ''}`}>
      <span className={`vx-meter-fill${over ? ' is-over' : ''}`} style={{ width: `${pctOfDb(db)}%` }} />
      {gateDb != null ? <span className="vx-meter-gate" style={{ left: `${pctOfDb(gateDb)}%` }} title="Microphone sensitivity" /> : null}
    </div>
  );
}

export default function AudioDeviceSettings({ dev }) {
  const [testing, setTesting] = useState(false);
  const meter = useMicMeter(testing, dev.inputId, { announce: true });
  const sinkOk = canSetSinkId();

  return (
    <Section
      id="vx-cat-audio"
      title="Audio Devices"
      description="Which microphone and speakers Voxemera uses in this browser. Changes apply straight away, also during a call."
      actions={
        <button type="button" className="vx-btn small" onClick={() => dev.refresh()} disabled={dev.loading || !dev.hasEnumerate}>
          {dev.loading ? 'Refreshing…' : 'Refresh devices'}
        </button>
      }
    >
      {dev.needsMicPermission ? (
        <div className="vx-set-note" role="status">
          Device names are hidden until you allow microphone access.{' '}
          <button type="button" className="vx-link-btn" onClick={() => dev.requestPermission('audio')}>
            Allow microphone
          </button>
        </div>
      ) : null}
      <Row label="Microphone" settingId="input-device" scope="device" onReset={() => dev.setInputId('')} isDefault={!dev.inputId} resetLabel="Use system default">
        <DeviceSelect label="Microphone" value={dev.inputId} options={dev.inputOptions} onChange={dev.setInputId} disabled={!dev.hasEnumerate} emptyText="No microphones found" testId="settings-input-device" />
      </Row>
      <Row
        label="Speakers / headphones"
        hint={sinkOk ? 'Used for voices, the soundboard and sound effects.' : 'This browser always plays through the system output. Choose the device in your system sound settings.'}
        settingId="output-device"
        scope="device"
        onReset={() => dev.setOutputId('')}
        isDefault={!dev.outputId}
        resetLabel="Use system default"
      >
        <DeviceSelect label="Speakers or headphones" value={dev.outputId} options={dev.outputOptions} onChange={dev.setOutputId} disabled={!sinkOk} emptyText="No outputs found" testId="settings-output-device" />
      </Row>
      <Row label="Microphone test" hint="Speak normally. The bar turns green above the sensitivity line (set under Voice & Video): that is when others hear you." settingId="mic-test" scope="device">
        <div className="vx-meter-row">
          <button type="button" className={`vx-btn small${testing ? ' is-on' : ''}`} aria-pressed={testing} onClick={() => setTesting((v) => !v)} data-testid="mic-test-toggle">
            {testing ? 'Stop test' : 'Test microphone'}
          </button>
          <LevelMeter db={testing ? meter.db : -100} gateDb={dev.gateDb} />
          <span className="vx-slider-value">{testing ? `${Math.round(meter.db)} dB` : '—'}</span>
        </div>
        {meter.error ? (
          <div className="vx-acct-error" role="alert">
            {meter.error}
          </div>
        ) : null}
      </Row>
      <Row label="Browser permissions" hint="Voxemera asks only when it needs a device. If access was blocked, allow it from the padlock in the address bar." settingId="permissions" scope="device">
        <div className="vx-inline">
          <button type="button" className="vx-btn small" onClick={() => dev.requestPermission('audio')} disabled={dev.loading || !dev.hasGetUserMedia}>
            Check microphone access
          </button>
          <button type="button" className="vx-btn small" onClick={() => dev.requestPermission('video')} disabled={dev.loading || !dev.hasGetUserMedia}>
            Check camera access
          </button>
        </div>
      </Row>
      {dev.permissionError || dev.deviceError || !dev.hasGetUserMedia ? (
        <div className="vx-acct-error" role="alert">
          {dev.permissionError || dev.deviceError || 'This browser cannot use microphones or cameras.'}
        </div>
      ) : null}
    </Section>
  );
}
